import { IMAGES } from "@/site";
import { PageHero, Section, SectionTitle, BulletGrid, CTABand } from "@/components/ui-blocks/Blocks";
import { Trophy, BadgeCheck, Truck, Radio } from "lucide-react";

const EXPERIENCE = [
  "School bus operations",
  "Student transportation safety",
  "Driver coaching and development",
  "Transportation dispatch operations",
  "Pre-trip inspection instruction",
  "Defensive driving",
  "Mirror use and blind spot awareness",
  "Loading and unloading procedures",
  "Railroad crossing procedures",
  "Emergency evacuation procedures",
  "Vehicle control training",
  "Incident-based corrective retraining",
];

const HIGHLIGHTS = [
  "School bus transportation experience since 2005",
  "Certified Delaware School Bus Driver Trainer since 2017",
  "Class A commercial driving experience across 48 states",
  "Dispatch Specialist experience",
  "School Bus Rodeo participant",
  "2nd place among district drivers",
  "Driver retraining and corrective coaching experience",
  "Pre-trip inspection training experience",
  "Safety-first training philosophy",
  "Real-world transportation operations knowledge",
  "Workforce development focus",
];

const TIMELINE = [
  { year: "2005", title: "School Transportation Career Begins", body: "Started building hands-on experience in school bus operations." },
  { year: "2017", title: "Certified Delaware School Bus Driver Trainer", body: "Began directly training and developing new drivers entering the industry." },
  { year: "48 States", title: "Class A Tractor Trailer Experience", body: "Broadened knowledge of commercial vehicle safety and professional driving standards." },
  { year: "Dispatch", title: "Dispatch Specialist Role", body: "Manages communication, bus assignments, and real-time decision-making during daily operations." },
  { year: "2nd Place", title: "School Bus Rodeo — District Drivers", body: "Demonstrated advanced vehicle control, safety awareness, and professional skill under competition standards." },
];

export default function Founder() {
  return (
    <>
      <PageHero
        testid="founder-hero"
        eyebrow="Meet the Founder"
        title="Curtis Brown"
        description="Founder · Owner · Trainer at CLB Solution LLC. Nearly two decades of school bus experience and a strong commitment to safety, driver development, and workforce training."
        image={IMAGES.founder}
      />

      <Section testid="founder-bio">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative border border-white/10 overflow-hidden">
              <img src={IMAGES.founder} alt="Curtis Brown, Founder of CLB Solution LLC" className="w-full h-auto object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black to-transparent">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">Founder</div>
                <div className="font-display text-2xl font-bold uppercase">Curtis Brown</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[
                { icon: Truck, label: "Since", value: "2005" },
                { icon: BadgeCheck, label: "Certified", value: "2017" },
                { icon: Radio, label: "Dispatch", value: "Active" },
                { icon: Trophy, label: "Rodeo", value: "2nd Place" },
              ].map((s) => (
                <div key={s.label} className="bg-[#09090b] p-5">
                  <s.icon size={20} className="text-[#FFCC00] mb-2" />
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">{s.label}</div>
                  <div className="font-display text-lg font-bold uppercase">{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5 text-white/75 leading-relaxed">
            <p>
              CLB Solution LLC was founded by Curtis Brown, a transportation professional with nearly
              two decades of school bus experience and a strong commitment to safety, driver
              development, and workforce training.
            </p>
            <p>
              Curtis began his school transportation career in 2005 and has spent years building
              practical experience in school bus operations, driver training, and transportation
              support. His career has been built on the understanding that school bus driving is one
              of the most important safety roles in the community.
            </p>
            <p>
              In 2017, Curtis became a <span className="text-[#FFCC00]">Certified Delaware School Bus Driver Trainer</span>, allowing him to directly train and develop new drivers entering the industry.
            </p>
            <p>
              In addition to school transportation, Curtis also gained experience as a Class A
              over-the-road tractor trailer driver, traveling across all 48 states. This broadened
              his knowledge of commercial vehicle safety, road awareness, and professional driving
              standards.
            </p>
            <p>
              Curtis has also served as a Dispatch Specialist, managing transportation communication,
              bus assignments, driver support, and real-time decision-making during daily operations.
            </p>
            <p>
              A major highlight of his driving career includes participating in the
              <span className="text-[#FFCC00]"> School Bus Rodeo</span>, where he earned
              <span className="text-[#FFCC00]"> 2nd place among district drivers</span>, demonstrating
              advanced vehicle control, safety awareness, and professional skill under competition
              standards.
            </p>
            <p className="text-white/60">
              His experience behind the wheel, in the training seat, and in dispatch operations gives
              him a full-circle understanding of school transportation from multiple critical
              perspectives.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="founder-timeline">
        <SectionTitle eyebrow="Career Highlights" title="A career built behind the wheel and beside trainees." />
        <ol className="relative border-l-2 border-[#FFCC00]/30 ml-4 space-y-10">
          {TIMELINE.map((t, i) => (
            <li key={i} className="pl-8 relative">
              <span className="absolute -left-[11px] top-1 h-5 w-5 bg-[#FFCC00] border-4 border-[#0c0c0e]" />
              <div className="font-mono text-xs text-[#FFCC00]">{t.year}</div>
              <h4 className="font-display text-xl md:text-2xl font-bold uppercase mt-1">{t.title}</h4>
              <p className="text-white/65 mt-2 max-w-2xl">{t.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section testid="founder-experience">
        <SectionTitle eyebrow="Hands-On Experience" title="Areas of practical expertise." />
        <BulletGrid items={EXPERIENCE} testid="founder-experience-grid" />
      </Section>

      <Section className="bg-[#0c0c0e] border-y border-white/10" testid="founder-highlights">
        <SectionTitle eyebrow="Experience Highlights" title="Credentials & milestones." />
        <BulletGrid items={HIGHLIGHTS} testid="founder-highlights-grid" />
      </Section>

      <Section testid="founder-philosophy">
        <div className="border border-white/10 p-8 md:p-14 bg-[#0c0c0e]">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4 font-semibold">Founding Belief</div>
          <h3 className="font-display text-2xl md:text-4xl font-bold uppercase leading-tight max-w-4xl">
            Safer drivers are created through patience, repetition, structure, accountability,
            practical experience, and clear communication.
          </h3>
        </div>
      </Section>

      <CTABand />
    </>
  );
}
